import { Component, h, useEffect, useRef, useState } from "@lukekaalim/act"
import { html } from "@lukekaalim/act-web"

import classes from './index.module.css';
import { renderBeat } from "@kosmod/graphics-3d";
import { ID, LOADING, ModalComponent, useAsync, useLogger, useRefresh } from "@kosmod/kit";
import { AuthState, GameID, gamesAPI, InvitationManager, ProfileOverlay, useGamesState } from "@kosmod/sesame";
import { LabeledInput } from "@kosmod/sesame";
import { useFrontendCore, useFrontendServices } from "@kosmod/core";
import { HttpApi } from "@kosmod/pancake";
import { conciergeApiDef } from "../../defs/api";
import { assetURLs } from "../../assets";

export type MyGamesProps = {
  clients: {
    concierge: HttpApi.Handler<'client', typeof conciergeApiDef>,
    games: HttpApi.Handler<'client', typeof gamesAPI>,
  },

  auth: AuthState,
}

export const MyGames: Component<MyGamesProps> = ({ clients, auth }) => {
  const [refresh, reloadKey] = useRefresh();

  const [selectedGameId, setSelectedGameId] = useState<GameID | null>(null);
  const backgroundRef = useRef<HTMLElement | null>(null);

  const logger = useLogger('MyGames');

  const tick = renderBeat.useTick();
  useEffect(() => {
    const animate = (now: DOMHighResTimeStamp) => {
      tick(now);
      handle = requestAnimationFrame(animate);
    }
    let handle = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(handle);
  })

  renderBeat.useBeat((now) => {
    const background = backgroundRef.get();
    if (!background)
      return;

    background.style.backgroundPosition = `${(-now / 50).toFixed(2)}px ${(now / 50).toFixed(2)}px`
  })
  const { modal, site } = useFrontendServices()

  // PRIMITIVE
  if (!auth.account)
    return 'NOT LOGGED IN';

  const games = useGamesState(clients.games);
  const finalSelectedGameID = selectedGameId || (games.all !== LOADING && games.all.length > 0 && games.all[0].id) || null

  const game = finalSelectedGameID && games.all !== LOADING && games.all.find(game => game.id === finalSelectedGameID) || null;

  const { result: rooms } = useAsync(async () => {
    if (!finalSelectedGameID)
      return null;

    const { rooms } = await clients.concierge.room.GET({
      gameId: finalSelectedGameID,
      roomId: null
    }, null);
    return rooms;
  }, [reloadKey, finalSelectedGameID])

  return h(html.div, { className: classes.myGames }, [
    h(html.div, { ref: backgroundRef, className: classes.background, style: { backgroundImage: `url(${assetURLs.isoGrid})` } }),
    h(ProfileOverlay, { account: auth.account, auth }),

    h(html.div, { className: classes.controls }, [
      games.all !== LOADING && h(html.div, { className: classes.gameSelectorControls }, [
        h(html.select, { className: classes.gameSelector, onInput(event) {
          setSelectedGameId(this.value as GameID)
        }, }, [
          !games.all.length && h(html.option, {}, 'No Game'),
          !!games.all.length && games.all.map(game =>
            h(html.option, { value: game.id, selected: game.id === selectedGameId }, game.name)),
        ]),
        h(html.button, { className: classes.joinGame }, [
          h(html.span, {}, 'join'),
          //h(html.img, { src: assetURLs.icons.joinWhite }),
        ]),
        h(html.button, { className: classes.newGame, async onClick() {
          const gameName = await modal.add(EnterNamePopup);
          if (gameName) {
            await games.createGame(gameName)
            refresh()
          }
        } }, [
          h(html.span, {}, 'new'),
          //h(html.img, { src: assetURLs.icons.newWhite }),
        ]),
      ]),

      games.all !== LOADING && h(html.div, { className: classes.selectedGameControls }, [
        !games.all.length && h(html.div, { className: classes.noGamesWelcomeBorder }, [
          h(html.section, { className: classes.noGamesWelcome }, [
            h(html.h2, {}, 'Welcome to Kosmod!'),
            h(html.p, {},
              `Create a New Game to get started,
              or use an Invitation Code if you have
              one to join an existing game.`
            ),
          ]),
        ]),
        (!rooms || !rooms.length) && h(html.div, { className: classes.noRoomsBorder }, [
          h(html.section, { className: classes.noRoomsWelcome }, [
            h(html.p, {},
              `Create a new Room to make a place to invite people to and play`
            ),
          ]),
        ]),
        !!rooms && !!finalSelectedGameID && h(html.ol, {}, rooms.map(room => h(html.li, {}, [
          h(html.div, {}, room.name),
          h(html.button, { onClick(event) {
            site.navigate({ path: '/room', query: { room: room.id, game: finalSelectedGameID, invite: null } })
          },}, 'Join')
        ]))),
        !!finalSelectedGameID && h(html.button, { async onClick(event) {
          const name = await modal.add(EnterNamePopup)
          if (!name)
            return;

          await clients.concierge.room.POST(null, { name, gameId: finalSelectedGameID })
          refresh();
        },}, "Create new Room"),
        !!game && h(InvitationManager, {
          game,
          games,
          refresh,
          refreshKey: reloadKey,
        })
      ]),
    ]),
    h(html.div, { className: classes.selectedGameInfo })
  ]);
}

const EnterNamePopup: ModalComponent<string> = ({ submit }) => {
  const [name, setName] = useState('');

  return h(html.form, { onSubmit(event: SubmitEvent) {
    event.preventDefault();
    submit(name)
  },}, [
    h(LabeledInput, {
      label: 'Enter new game name',
      name: 'gamename',
      placeholder: 'Cool Game',
      value: name,
      onInput(event) {
        setName(this.value);
      },
    }),
  ])
}